import { type Key, useCallback, useMemo, useRef } from "react";

import { useDispatch, useSelector } from "react-redux";

import type { ITableProps } from "@similarweb/pro-logic-components";

import { SearchResultsTable } from "app/pages/sales-intelligence/common-components/search-results-table";
import { useTableScrollHandlers } from "app/pages/sales-intelligence/common-components/search-results-table/hooks/useTableScrollHandlers";
import { useTableUnmountKey } from "app/pages/sales-intelligence/common-components/search-results-table/hooks/useTableUnmountKey";
import { useVirtualTableScrollY } from "app/pages/sales-intelligence/common-components/search-results-table/hooks/useVirtualTableScrollY";

import { setAppsTableRequestParams, setAppsSelectedRows } from "../../store/action-creators";
import initialAppsSearchState from "../../store/initial-state";
import {
    selectAppsSearchRequestDto,
    selectAppsIsRowSelectionEnabled,
    selectAppsSelectedRowKeys,
} from "../../store/selectors";
import type { AppsSelectedRow } from "../../store/types";
import type { AppSearchRowDto } from "../../types/rows";
import type { AppsSearchTableData } from "../../types/table";

import { StyledAppsTableWrapper } from "./styles";

const DEFAULT_TABLE_DATA: AppsSearchTableData = { columns: [], rows: [], totalCount: 0 };

type Props = {
    isLoading: boolean;
    data?: AppsSearchTableData;
};

const SearchResultsTableContainer = ({ isLoading, data = DEFAULT_TABLE_DATA }: Props) => {
    const dispatch = useDispatch();

    const tableWrapperRef = useRef<HTMLDivElement>(null);

    const appsSearchRequestDto = useSelector(selectAppsSearchRequestDto);
    const isRowSelectionEnabled = useSelector(selectAppsIsRowSelectionEnabled);
    const selectedRowKeys = useSelector(selectAppsSelectedRowKeys);

    const handleSortChange = useCallback<NonNullable<ITableProps<AppSearchRowDto>["onSort"]>>(
        ({ order, columnKey }) => {
            dispatch(
                setAppsTableRequestParams({
                    orderBy: columnKey?.toString(),
                    asc: order === "ascend",
                    page: initialAppsSearchState.tableParams.page,
                }),
            );
        },
        [dispatch],
    );

    const selectionProps: ITableProps<AppSearchRowDto>["selection"] = useMemo(
        () => ({
            enabled: isRowSelectionEnabled,
            selectedRowKeys: selectedRowKeys,
            onChange(_keys: Key[], rows: (AppSearchRowDto & { key: Key })[]) {
                dispatch(setAppsSelectedRows(rows as AppsSelectedRow[]));
            },
        }),
        [isRowSelectionEnabled, selectedRowKeys, dispatch],
    );

    const paginationProps: ITableProps<AppSearchRowDto>["pagination"] = useMemo(
        () => ({
            pageSize: appsSearchRequestDto.pageSize,
            total: data.totalCount,
            current: appsSearchRequestDto.page,
            onChange(_currentPage: number, _pageSize: number, nextPage) {
                dispatch(setAppsTableRequestParams({ page: nextPage }));
            },
        }),
        [appsSearchRequestDto.pageSize, appsSearchRequestDto.page, data.totalCount, dispatch],
    );

    const tableUnmountKey = useTableUnmountKey(data.columns);
    const scrollY = useVirtualTableScrollY(tableWrapperRef);
    const { onScroll } = useTableScrollHandlers(tableWrapperRef);

    return (
        <StyledAppsTableWrapper
            key={tableUnmountKey}
            data-automation="apps-table"
            ref={tableWrapperRef}
        >
            <SearchResultsTable<AppSearchRowDto>
                isLoading={isLoading}
                data={data.rows as AppSearchRowDto[]}
                columnConfig={data.columns}
                pagination={paginationProps}
                selection={selectionProps}
                virtual
                scroll={{ y: scrollY }}
                sticky={false}
                onSort={handleSortChange}
                onScroll={onScroll}
            />
        </StyledAppsTableWrapper>
    );
};

export default SearchResultsTableContainer;
